const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

let loadPromise = null;

/**
 * Load the Facebook JS SDK once. Resolves when window.FB is available.
 * FB.init must be called separately with backend config before FB.login.
 */
export function loadFacebookSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('Facebook SDK requires a browser environment.'),
    );
  }

  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const previousInit = window.fbAsyncInit;
    window.fbAsyncInit = function fbAsyncInit() {
      if (typeof previousInit === 'function') {
        previousInit();
      }
      if (window.FB) {
        resolve(window.FB);
      } else {
        loadPromise = null;
        reject(new Error('Facebook SDK loaded but FB is unavailable.'));
      }
    };

    if (document.getElementById('facebook-jssdk')) {
      // Script tag exists; wait for fbAsyncInit or FB.
      const poll = window.setInterval(() => {
        if (window.FB) {
          window.clearInterval(poll);
          resolve(window.FB);
        }
      }, 50);
      window.setTimeout(() => {
        window.clearInterval(poll);
        if (!window.FB) {
          loadPromise = null;
          reject(new Error('Timed out waiting for Facebook SDK.'));
        }
      }, 15000);
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = FACEBOOK_SDK_SRC;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Facebook SDK.'));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}

/**
 * Initialize FB with Embedded Signup config from the backend.
 */
export function initFacebookSdk(config) {
  if (!window.FB) {
    throw new Error('Facebook SDK is not loaded.');
  }

  window.FB.init({
    appId: String(config.app_id),
    cookie: true,
    autoLogAppEvents: true,
    xfbml: true,
    version: config.graph_version,
  });

  return window.FB;
}
